import { useEffect, useId, useMemo, useState } from 'react'
import { Copy, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useRotateCatalogSupplierMagicLink } from '@/shared/hooks/api/catalog'
import {
  isSupportedLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/shared/config/languages'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { withCatalogSupplierInviteLanguage } from '@/v2/utils/catalogSupplierInviteUrl'

type V2CatalogSupplierInviteDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierId: string
  magicLinkUrl: string
}

export function V2CatalogSupplierInviteDialog({
  open,
  onOpenChange,
  supplierId,
  magicLinkUrl,
}: V2CatalogSupplierInviteDialogProps) {
  const { t } = useTranslation('common')
  const rotateMutation = useRotateCatalogSupplierMagicLink(supplierId)
  const inviteLanguageGroupId = useId()
  const [inviteLanguage, setInviteLanguage] =
    useState<SupportedLanguage>('en-GB')
  const [currentMagicLinkUrl, setCurrentMagicLinkUrl] = useState(magicLinkUrl)

  useEffect(() => {
    if (!open) {
      return
    }
    setInviteLanguage('en-GB')
    setCurrentMagicLinkUrl(magicLinkUrl)
  }, [open, magicLinkUrl])

  const hasInviteUrl = currentMagicLinkUrl.trim().length > 0

  const inviteUrlWithLanguage = useMemo(() => {
    if (!hasInviteUrl) {
      return ''
    }
    return withCatalogSupplierInviteLanguage(
      currentMagicLinkUrl,
      inviteLanguage
    )
  }, [currentMagicLinkUrl, hasInviteUrl, inviteLanguage])

  async function copyInviteLink(url: string) {
    await navigator.clipboard.writeText(url)
    toast.success(t('v2.pages.suppliers.copyInviteLinkSuccess'))
  }

  async function handleRotate() {
    const rotated = await rotateMutation.mutateAsync()
    setCurrentMagicLinkUrl(rotated.magicLinkUrl)
    const rotatedWithLanguage = withCatalogSupplierInviteLanguage(
      rotated.magicLinkUrl,
      inviteLanguage
    )
    await navigator.clipboard.writeText(rotatedWithLanguage)
    toast.success(t('v2.pages.suppliers.invite.rotated'))
  }

  function openPortal(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('v2.pages.suppliers.invite.title')}</DialogTitle>
        </DialogHeader>
        <p className='text-sm text-muted-foreground'>
          {t('v2.pages.suppliers.invite.help')}
        </p>
        <Input readOnly value={inviteUrlWithLanguage} />
        <div className='space-y-2'>
          <Label id={`${inviteLanguageGroupId}-label`}>
            {t('v2.pages.suppliers.invite.languageLabel')}
          </Label>
          <RadioGroup
            value={inviteLanguage}
            onValueChange={(value) => {
              if (isSupportedLanguage(value)) {
                setInviteLanguage(value)
              }
            }}
            aria-labelledby={`${inviteLanguageGroupId}-label`}
            className='flex flex-wrap gap-3'
          >
            {SUPPORTED_LANGUAGES.map((language) => {
              const optionId = `${inviteLanguageGroupId}-${language.value}`
              return (
                <div key={language.value} className='flex items-center gap-2'>
                  <RadioGroupItem value={language.value} id={optionId} />
                  <Label
                    htmlFor={optionId}
                    className='cursor-pointer font-normal'
                  >
                    <span className='flex items-center gap-2'>
                      <span className='text-xl leading-none' aria-hidden>
                        {language.flag}
                      </span>
                      <span>{language.label}</span>
                    </span>
                  </Label>
                </div>
              )
            })}
          </RadioGroup>
        </div>
        <div className='flex w-full flex-row flex-wrap items-center justify-between gap-2'>
          <Button
            type='button'
            variant='destructive'
            onClick={() => {
              void handleRotate()
            }}
            disabled={rotateMutation.isPending || !hasInviteUrl}
          >
            {t('v2.pages.suppliers.invite.rotate')}
          </Button>
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant='outline'
              className='gap-2'
              disabled={!hasInviteUrl}
              onClick={() => {
                openPortal(inviteUrlWithLanguage)
              }}
            >
              <ExternalLink className='size-4' />
              {t('v2.pages.suppliers.invite.openPortal')}
            </Button>
            <Button
              type='button'
              className='gap-2'
              disabled={!hasInviteUrl}
              onClick={() => {
                void copyInviteLink(inviteUrlWithLanguage)
              }}
            >
              <Copy className='size-4' />
              {t('v2.pages.suppliers.invite.copy')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
